// src/components/store.jsx
// Paystack removed. Monnify SDK used for all asset purchases.
// formatNaira imported from useSubscription (single source of truth).

import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, doc, updateDoc, arrayUnion, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { formatNaira } from '../hooks/useSubscription';

const GOLD = '#B8860B';

export default function Store() {
  const [assets,         setAssets]         = useState([]);
  const [selectedAsset,  setSelectedAsset]  = useState(null);
  const [userPurchases,  setUserPurchases]  = useState([]);
  const [processing,     setProcessing]     = useState(false);
  const [error,          setError]          = useState(null);

  useEffect(() => {
    const unsubAssets = onSnapshot(collection(db, 'globalAssets'), snap => {
      setAssets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    let unsubUser = () => {};
    if (auth.currentUser) {
      unsubUser = onSnapshot(doc(db, 'barbers', auth.currentUser.uid), snap => {
        setUserPurchases(snap.data()?.purchasedAssets || []);
      });
    }

    return () => { unsubAssets(); unsubUser(); };
  }, []);

  // ── Finalize purchase in Firestore ─────────────────────────────────────────
  const finalizePurchase = async (asset) => {
    try {
      const userRef = doc(db, 'barbers', auth.currentUser.uid);
      await updateDoc(userRef, { purchasedAssets: arrayUnion(asset.id) });

      await addDoc(collection(db, 'store_sales'), {
        assetId:   asset.id,
        assetName: asset.name,
        amount:    asset.price,
        userId:    auth.currentUser.uid,
        method:    'monnify_card',
        createdAt: serverTimestamp(),
      });

      alert(`${asset.name} Unlocked! It is now available in your toolkit.`);
      setSelectedAsset(null);
    } catch {
      setError('Purchase recorded, but account update failed. Please contact support.');
    }
  };

  // ── Monnify payment ────────────────────────────────────────────────────────
  const handleMonnifyPayment = () => {
    if (!selectedAsset) return;
    setError(null);

    if (!window.MonnifySDK) {
      setError('Payment gateway not loaded. Please refresh the page.');
      return;
    }

    const apiKey       = process.env.REACT_APP_MONNIFY_API_KEY;
    const contractCode = process.env.REACT_APP_MONNIFY_CONTRACT_CODE;

    if (!apiKey || !contractCode) {
      setError('Payment gateway not configured. Please contact support.');
      return;
    }

    setProcessing(true);

    window.MonnifySDK.initialize({
      amount:             selectedAsset.price,
      currency:           'NGN',
      reference:          `STR_${auth.currentUser.uid.slice(0, 6)}_${Date.now()}`,
      customerFullName:   auth.currentUser.displayName || 'Esdras User',
      customerEmail:      auth.currentUser.email       || 'user@esdras.app',
      apiKey,
      contractCode,
      paymentDescription: `Esdras Asset: ${selectedAsset.name}`,
      metadata: {
        assetId: selectedAsset.id,
        userId:  auth.currentUser.uid,
        type:    'asset_purchase',
      },
      onComplete: async (response) => {
        setProcessing(false);
        if (response.status === 'SUCCESS') {
          await finalizePurchase(selectedAsset);
        } else {
          setError('Payment was not completed. Please try again.');
        }
      },
      onClose: () => setProcessing(false),
    });
  };

  return (
    <div style={storeGrid}>
      <h2 style={header}>ASSET MARKETPLACE</h2>

      {assets.map(asset => {
        const isOwned = userPurchases.includes(asset.id);
        return (
          <div key={asset.id} style={assetCard}>
            <img src={asset.previewUrl} style={previewImg} alt={asset.name} />
            <div style={info}>
              <h4 style={{ margin: '0 0 4px', color: 'white' }}>{asset.name}</h4>
              <p style={priceTag}>{formatNaira(asset.price)}</p>
              {isOwned
                ? <button disabled style={ownedBtn}>OWNED</button>
                : <button onClick={() => setSelectedAsset(asset)} style={buyBtn}>UNLOCK</button>
              }
            </div>
          </div>
        );
      })}

      {/* MONNIFY PAYMENT MODAL */}
      {selectedAsset && (
        <div style={paymentOverlay}>
          <div style={paymentModal}>
            <h3 style={{ color: GOLD, marginTop: 0 }}>Unlock {selectedAsset.name}</h3>
            <p style={{ fontSize: '1.4rem', fontWeight: '900', color: 'white', margin: '0 0 16px' }}>
              {formatNaira(selectedAsset.price)}
            </p>

            {error && (
              <p style={{ color: '#ff6b6b', fontSize: '0.8rem', marginBottom: '12px' }}>{error}</p>
            )}

            <button
              onClick={handleMonnifyPayment}
              disabled={processing}
              style={{ ...buyBtn, padding: '15px', marginBottom: '10px', opacity: processing ? 0.7 : 1 }}
            >
              {processing ? 'VERIFYING...' : `PAY ${formatNaira(selectedAsset.price)} VIA MONNIFY`}
            </button>
            <button
              onClick={() => { setSelectedAsset(null); setError(null); }}
              style={{ background: 'none', border: 'none', color: 'white', marginTop: '10px', cursor: 'pointer', display: 'block', width: '100%' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const storeGrid      = { padding:'20px', background:'#000', minHeight:'100vh', color:'white', fontFamily:'Montserrat, sans-serif' };
const header         = { color:GOLD, textAlign:'center', letterSpacing:'4px', marginBottom:'30px' };
const assetCard      = { background:'#001F3F', borderRadius:'15px', marginBottom:'15px', overflow:'hidden', display:'flex' };
const previewImg     = { width:'100px', height:'100px', objectFit:'cover', flexShrink:0 };
const info           = { padding:'15px', flex:1, display:'flex', flexDirection:'column', justifyContent:'center' };
const priceTag       = { color:GOLD, fontWeight:'bold', margin:'4px 0 8px' };
const buyBtn         = { background:GOLD, border:'none', padding:'10px', borderRadius:'8px', fontWeight:'bold', cursor:'pointer', width:'100%', color:'black' };
const ownedBtn       = { background:'#333', color:'#777', border:'none', padding:'10px', borderRadius:'8px', width:'100%', cursor:'default' };
const paymentOverlay = { position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', display:'grid', placeItems:'center', zIndex:1000 };
const paymentModal   = { background:'#001F3F', padding:'2.5rem', borderRadius:'24px', border:`1px solid ${GOLD}`, textAlign:'center', width:'320px', maxWidth:'90%' };
